import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Ambulance,
  ArrowRight,
  BookOpen,
  Car,
  Phone,
  Scissors,
  Search,
  ShoppingBag,
  Stethoscope,
  Store,
  Users,
  Utensils,
  Wrench,
  Zap,
} from "lucide-react";
import { usePageTitle } from "@/lib/usePageTitle";
import { useApp } from "@/state/AppContext";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { payText } from "@/components/JobCard";
import { BusinessCard } from "@/components/BusinessCard";
import { CityAbout } from "@/components/CityAbout";
import { CityAdSlot } from "@/components/CityAdSlot";
import { ResumeStrip } from "@/components/home/ResumeStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { mediaSrcSet, mediaVariant, resolveImage } from "@/lib/media";
import { advertisedBusinessPath } from "@/lib/whiteLabel";
import { shortDate, tradeLabel } from "@/lib/format";

const VISITED_KEY = "city_home_visited";
const FEED_KICKERS = { offers: "feedKickerOffer", new_this_week: "feedKickerNew", events: "feedKickerEvent" };
const FEED_KICKER_TONE = { offers: "text-[hsl(var(--accent))]", new_this_week: "text-[hsl(var(--primary))]", events: "text-muted-foreground" };

// Category → lucide icon, matched by keyword in the slug. Falls back to a generic storefront. No
// per-city hardcoding — this is a display heuristic over the tenant's own categories.
const CATEGORY_ICONS = [
  [/salon|beauty|hair/, Scissors],
  [/electric|electron/, Zap],
  [/food|restaurant|dhaba|bhojan|eat|cafe/, Utensils],
  [/edu|tuition|coach|school|book/, BookOpen],
  [/grocery|kirana|shop|retail|store/, ShoppingBag],
  [/doctor|clinic|health|medic|hospital/, Stethoscope],
  [/repair|plumb|mechanic|service/, Wrench],
  [/transport|taxi|auto|car|travel/, Car],
  [/worker|labour|labor|staff/, Users],
];
function categoryIcon(slug) {
  const hit = CATEGORY_ICONS.find(([re]) => re.test(slug || ""));
  return hit ? hit[1] : Store;
}

function feedRowText(section, item, lang, t) {
  if (section === "offers") {
    return { title: localized(lang, item, "title"), meta: `${item.business_name || ""} · ${t("offers.endsOn", { date: shortDate(item.ends_on, lang) || item.ends_on })}` };
  }
  if (section === "events") {
    return { title: localized(lang, item, "title"), meta: localized(lang, item, "venue") };
  }
  return { title: localized(lang, item, "name"), meta: localized(lang, item, "description") || localized(lang, item, "address") };
}

/** City Home redesign (design_handoff_monetization "City Home"): hero left + bookable ad slot right,
 * category icon-tile grid with self-registration chips, a full-width leaderboard slot under the
 * hero, shops ("Loved in") before the feed with Premium businesses first, a sponsored rail card,
 * an offers-live-now promoted box, and an about ad square. Reuses BusinessCard / CityAdSlot /
 * CityAbout and the existing city data endpoints. */
export default function CityHome() {
  const { t, lang, tenant, auth, getCategories } = useApp();
  const navigate = useNavigate();
  // The vacant ad-slot placeholder (weekly rate + "talk to the city desk") and the rail house
  // card's "advertise" line are operator-facing sales prompts (monetization slice 5), never for a
  // citizen reading the public city home. Show them only to someone who can actually sell a slot —
  // a platform admin or an operator seat-holder. A plain citizen sees empty slots with no rate-card
  // text (the rail keeps its citizen-facing "Book a doctor" card, minus the advertise line).
  const canSellAds = !!(
    auth &&
    (auth.is_platform_admin ||
      auth.active_role_type === "operator" ||
      auth.role_profiles?.some((rp) => rp.role_type === "operator"))
  );
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [feedSections, setFeedSections] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [landmarks, setLandmarks] = useState([]);
  const [stats, setStats] = useState(null);
  const [ads, setAds] = useState({ hero: null, rail: null, leaderboard: null, about: null, rates: {} });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState([]);
  const [howOpen, setHowOpen] = useState(true);

  const load = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    const fetchers = {
      categories: () => getCategories(),
      businesses: () => api.get("/directory/businesses", { params: { tenant_id: tenant.id, limit: 50 } }),
      feed: () => api.get("/city/feed", { params: { tenant_id: tenant.id } }),
      jobs: () => api.get("/city/jobs", { params: { tenant_id: tenant.id, limit: 100 } }),
      landmarks: () => api.get("/city/landmarks", { params: { tenant_id: tenant.id } }),
      stats: () => api.get("/directory/stats", { params: { tenant_id: tenant.id } }),
      ads: () => api.get("/city/ads", { params: { tenant_id: tenant.id } }),
    };
    const keys = Object.keys(fetchers);
    const setters = {
      categories: setCategories,
      businesses: setBusinesses,
      feed: (data) => setFeedSections(data.sections || []),
      jobs: setJobs,
      landmarks: setLandmarks,
      stats: setStats,
      ads: setAds,
    };
    const results = await Promise.allSettled(keys.map((k) => fetchers[k]()));
    const nextFailed = [];
    results.forEach((res, i) => {
      const key = keys[i];
      if (res.status === "fulfilled") setters[key](res.value.data);
      else nextFailed.push(key);
    });
    setFailed(nextFailed);
    setLoading(false);
  }, [tenant, getCategories]);

  useEffect(() => {
    load();
  }, [load]);

  // RF-21: derived, not synced through an effect - the effect left `about` empty on the first
  // paint, so the "no intro yet" copy flashed before the city's own text.
  const about = lang === "hi" ? tenant?.about_hi || tenant?.about || "" : tenant?.about || "";

  useEffect(() => {
    setHowOpen(!localStorage.getItem(VISITED_KEY));
    localStorage.setItem(VISITED_KEY, "1");
  }, []);

  useEffect(() => {
    if (!tenant || query.trim().length < 2) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }
    const handle = setTimeout(async () => {
      const { data } = await api.get("/directory/suggest", { params: { tenant_id: tenant.id, q: query } });
      setSuggestions(rows(data));
      setActiveIndex(-1);
    }, 220);
    return () => clearTimeout(handle);
  }, [query, tenant]);

  const cityName = localized(lang, tenant, "name");
  usePageTitle(cityName ? t("home.pageTitle", { city: cityName }) : null);
  // Suspended is a tenant-level fact already carried by GET /tenants/resolve (ADM-NW-03 style
  // suspension, not a per-feature flag) — informational content (listings, search, the feed)
  // stays read-only; only the CTAs that would create new activity are disabled, so the feature
  // reads as paused, not broken. Admin/operator access is untouched by this — that's 7356c25a's.
  // The city's own hero picture (with its tagline over it) fills the hero slot while nobody has
  // bought it; a sold slot shows the ad as before. Nothing to show -> no hero fallback at all.
  const heroTagline = localized(lang, tenant, "tagline");
  const cityHero = tenant?.hero_image_url ? (
    <div className="relative aspect-[5/4] w-full max-w-[340px] overflow-hidden rounded-2xl border border-border bg-muted" data-testid="home-city-hero">
      <img
        src={resolveImage(mediaVariant(tenant.hero_image_url, "card"))}
        srcSet={mediaSrcSet(tenant.hero_image_url) || undefined}
        sizes="(min-width: 1024px) 340px, 100vw"
        alt={cityName || ""}
        className="h-full w-full object-cover"
      />
      {heroTagline && (
        <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-3 pt-8 text-sm font-semibold text-white" data-testid="home-city-hero-tagline">
          {heroTagline}
        </p>
      )}
    </div>
  ) : null;
  const suspended = tenant?.status === "suspended";
  const disabledCtaClass = "pointer-events-none cursor-not-allowed opacity-50";

  const goToSuggestion = (s) => {
    setSuggestions([]);
    navigate(s.type === "business" ? advertisedBusinessPath(s) : `/w/${s.id}`);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }
    if (suggestions.length === 0) {
      if (e.key === "Enter") navigate(`/directory?q=${encodeURIComponent(query)}`);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) goToSuggestion(suggestions[activeIndex]);
      else navigate(`/directory?q=${encodeURIComponent(query)}`);
    }
  };

  const categoryCounts = {};
  businesses.forEach((b) => (b.category_ids || []).forEach((id) => (categoryCounts[id] = (categoryCounts[id] || 0) + 1)));
  // RF-11: the public listing rows carry no `category_ids`, so counting them here was always 0 and
  // the grid vanished. The city's own count rides on the category (`listing_count`); the local
  // tally is only the fallback for a payload without it.
  const countFor = (c) => c.listing_count ?? categoryCounts[c.id] ?? 0;
  const nonEmptyCategories = categories.filter((c) => countFor(c) > 0);
  const tileCategories = [...nonEmptyCategories]
    // UAT D2: a category no listing sits in is a dead tile — never shown.
    .sort((a, b) => countFor(b) - countFor(a)).slice(0, 10);

  const feedRows = ["offers", "new_this_week", "events"]
    .flatMap((key) => {
      const section = feedSections.find((s) => s.key === key);
      return (section?.items || []).slice(0, 2).map((item) => ({ key, item }));
    })
    .slice(0, 6);

  // L1-002: "Promoted" must mean real paid promotion (the backend flags an offer `promoted`
  // only when its business currently holds a sold, in-window city-home ad slot —
  // `directory/service.py::_promoted_business_ids`). Never label unpaid content as promoted —
  // that is an ASCI/trust issue, not a display detail. When nothing is actually promoted, the
  // box falls back to a plain "Offers live now" list with no "Promoted" chip.
  const allLiveOffers = feedSections.find((s) => s.key === "offers")?.items || [];
  const promotedOffers = allLiveOffers.filter((o) => o.promoted).slice(0, 4);
  const hasPromotedOffers = promotedOffers.length > 0;
  const liveOffers = hasPromotedOffers ? promotedOffers : allLiveOffers.slice(0, 4);
  const availableWorkers = (feedSections.find((s) => s.key === "available_workers")?.items || []).slice(0, 2);
  // Premium businesses first (server already rotates them); ✦ badge is rendered by BusinessCard.
  const featuredBusinesses = businesses.slice(0, 6);
  // D-02: "Loved in" implies reviews; if none of the featured shops has any rating yet, use a
  // neutral "Shops in" heading instead of claiming affection nobody has expressed.
  const featuredHasReviews = featuredBusinesses.some((b) => (b.rating_count || 0) > 0);

  return (
    <div className="flex flex-col">
      {suspended && (
        <div
          data-testid="home-suspended-banner"
          className="border-b border-border bg-muted px-5 py-2.5 text-center text-sm text-muted-foreground"
        >
          {t("home.suspendedBanner")}
        </div>
      )}

      {/* HERO — left column + bookable ad slot right */}
      <section className="border-b border-border bg-[var(--brand-cream,#f7f3ec)]">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-5 py-9 sm:px-8 lg:grid-cols-[1fr_340px]">
          <div className="flex min-w-0 max-w-2xl flex-col gap-4">
            {/* The city's hero picture is the banner behind the headline + search band (the picture is
                decoration - the text carries the meaning - hence alt=""). `sizes` is the column
                width, so a phone fetches `card`, a wide screen `detail`. */}
            <div className={`relative flex flex-col gap-4 ${tenant?.hero_image_url ? "rounded-3xl p-5 sm:p-7" : ""}`} data-testid="home-hero-band">
              {tenant?.hero_image_url && (
                <div className="absolute inset-0 overflow-hidden rounded-3xl bg-muted" aria-hidden="true" data-testid="home-hero-banner">
                  <img
                    src={resolveImage(mediaVariant(tenant.hero_image_url, "detail"))}
                    srcSet={mediaSrcSet(tenant.hero_image_url) || undefined}
                    sizes="(min-width: 1024px) 700px, 100vw"
                    alt=""
                    loading="eager"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/20" />
                </div>
              )}
            <span className={`relative w-fit rounded-full border px-4 py-1 text-xs font-semibold ${tenant?.hero_image_url ? "border-white/40 bg-white/15 text-white" : "border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"} ${lang === "en" ? "uppercase tracking-[0.2em]" : ""}`}>
              {t("home.heroKicker")}
            </span>
            <h1 data-testid="hero-title" className={`relative font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl ${tenant?.hero_image_url ? "text-white" : ""}`}>
              {t("home.heroTitle", { city: cityName })}
            </h1>

            <div className="relative">
              <div className="flex items-center gap-2 rounded-full border border-border bg-card p-2 shadow-sm">
                <Search size={18} className="ml-3 text-muted-foreground" />
                <Input
                  data-testid="home-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={t("common.searchPlaceholder")}
                  role="combobox"
                  aria-expanded={suggestions.length > 0}
                  aria-controls="home-suggestions"
                  aria-autocomplete="list"
                  className="border-0 bg-transparent shadow-none focus-visible:ring-0"
                />
                <Button className="rounded-full px-5" data-testid="home-search-btn" onClick={() => navigate(`/directory?q=${encodeURIComponent(query)}`)}>
                  {t("common.search")}
                </Button>
              </div>
              {suggestions.length > 0 && (
                <ul id="home-suggestions" role="listbox" data-testid="home-suggestions" className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
                  {suggestions.map((s, idx) => (
                    <li key={`${s.type}-${s.id}`} role="option" aria-selected={idx === activeIndex}>
                      <button
                        data-testid={`suggestion-${s.id}`}
                        onClick={() => goToSuggestion(s)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        className={`flex w-full items-center justify-between px-5 py-3 text-left text-sm transition-colors duration-200 ${idx === activeIndex ? "bg-muted" : "hover:bg-muted"}`}
                      >
                        <span>{lang === "hi" ? s.label_hi : s.label}</span>
                        <ArrowRight size={14} className="text-muted-foreground" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            </div>

            {/* Category icon-tile grid */}
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}
              data-testid="home-category-grid"
            >
              <Link
                to="/ambulance"
                data-testid="home-category-ambulance"
                className="flex items-center gap-2.5 rounded-xl border border-destructive/30 bg-destructive/[0.06] p-3 transition-transform duration-200 hover:-translate-y-0.5"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-destructive/12 text-destructive">
                  <Ambulance size={16} />
                </span>
                <span className="text-[13.5px] font-semibold text-destructive">{t("home.verticalAmbulance")}</span>
              </Link>
              {tileCategories.map((c) => {
                const Icon = categoryIcon(c.slug);
                return (
                  <Link
                    key={c.id}
                    to={`/directory?category_id=${c.id}`}
                    data-testid={`home-category-tile-${c.slug}`}
                    className="flex items-center gap-2.5 rounded-xl border border-border bg-card p-3 transition-transform duration-200 hover:-translate-y-0.5"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                      <Icon size={16} />
                    </span>
                    <span className="min-w-0 break-words text-[13.5px] leading-tight">{localized(lang, c, "name")}</span>
                  </Link>
                );
              })}
              {/* RF-21: no "All 0 categories" before the categories have loaded. */}
              {!loading && nonEmptyCategories.length > 0 && (
              <Link
                to="/directory"
                data-testid="home-category-all"
                className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-border p-3 text-[13.5px] font-medium text-[hsl(var(--primary))] transition-transform duration-200 hover:-translate-y-0.5"
              >
                {t("home.allCategories", { count: nonEmptyCategories.length })} <ArrowRight size={14} />
              </Link>
              )}
            </div>

            {/* Self-registration chips */}
            <div className="flex flex-wrap gap-2">
              <Link
                to="/onboarding-request"
                data-testid="home-register-shop"
                aria-disabled={suspended}
                onClick={(e) => suspended && e.preventDefault()}
                className={`inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3.5 py-2 text-sm font-semibold text-[hsl(var(--primary))] transition-transform duration-200 hover:-translate-y-0.5 ${suspended ? disabledCtaClass : ""}`}
              >
                <Store size={14} /> {t("home.registerShop")}
              </Link>
              <Link
                to="/join-worker"
                data-testid="home-register-worker"
                aria-disabled={suspended}
                onClick={(e) => suspended && e.preventDefault()}
                className={`inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3.5 py-2 text-sm font-semibold text-[hsl(var(--primary))] transition-transform duration-200 hover:-translate-y-0.5 ${suspended ? disabledCtaClass : ""}`}
              >
                <Users size={14} /> {t("home.joinWorkerApp")}
              </Link>
            </div>

            {stats ? (
              (stats.businesses || stats.workers || stats.reviews) ? <p className="text-sm text-muted-foreground" data-testid="home-stats-line">
                {t("home.statsLine", { businesses: stats.businesses, workers: stats.workers, reviews: stats.reviews, city: cityName })}
              </p> : null
            ) : loading ? (
              <Skeleton className="h-4 w-64" />
            ) : null}

            {failed.length > 0 && (
              <div data-testid="home-load-error" className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive">
                {t("home.couldntLoad")}
                <Button size="sm" variant="outline" className="rounded-full" data-testid="home-load-retry" onClick={load}>
                  {t("common.retry")}
                </Button>
              </div>
            )}
          </div>

          {/* Hero right column: bookable hero ad slot + offers-live-now */}
          <aside className="flex min-w-0 flex-col gap-4">
            <CityAdSlot ad={ads.hero} slot="hero" testId="home-ad-hero" rate={ads.rates?.hero} showPlaceholder={canSellAds} canSellAds={canSellAds} fallback={cityHero} fallbackImage={tenant?.hero_image_url} />
            <div className="rounded-2xl border border-border bg-card p-4" data-testid="home-offers-live">
              <div className="flex items-center justify-between">
                <h3 className={`text-xs font-semibold uppercase text-muted-foreground ${lang === "en" ? "tracking-[0.12em]" : ""}`}>{t("home.offersLiveTitle")}</h3>
                {hasPromotedOffers && (
                  <span
                    className="rounded-full bg-[hsl(var(--accent))]/12 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-[hsl(31_97%_40%)]"
                    data-testid="home-offers-promoted-badge"
                  >
                    {t("home.promoted")}
                  </span>
                )}
              </div>
              {/* R3-10: loading is not empty - no "No offers live" until the feed has answered. */}
              {loading ? (
                <Skeleton className="mt-3 h-10 w-full" data-testid="home-offers-live-loading" />
              ) : liveOffers.length === 0 ? (
                <p className="mt-3 text-[13px] text-muted-foreground">{t("home.offersLiveEmpty")}</p>
              ) : (
                <div className="mt-2 flex flex-col divide-y divide-border">
                  {liveOffers.map((o) => (
                    <Link key={o.id} to={advertisedBusinessPath({ slug: o.business_slug })} data-testid={`home-offer-${o.id}`} className="flex flex-col py-2.5">
                      <span className="text-sm font-semibold leading-snug">{localized(lang, o, "title")}</span>
                      <span className="mt-0.5 text-xs text-muted-foreground">{o.business_name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* Full-width leaderboard slot directly under the hero */}
      <section className="mx-auto w-full max-w-7xl px-5 pt-7 sm:px-8">
        <CityAdSlot ad={ads.leaderboard} slot="leaderboard" testId="home-ad-leaderboard" rate={ads.rates?.leaderboard} showPlaceholder={canSellAds} canSellAds={canSellAds} />
      </section>

      {/* Body: shops first, then feed; rail on the right */}
      <div className="mx-auto grid w-full max-w-7xl gap-7 px-5 py-7 sm:px-8 lg:grid-cols-[1.55fr_1fr]">
        <div className="flex min-w-0 flex-col gap-6">
          <ResumeStrip />

          <section>
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-heading text-xl font-semibold tracking-tight">{t(featuredHasReviews ? "home.lovedTitle" : "home.popularTitle", { city: cityName })}</h2>
              {(loading || featuredBusinesses.length > 0) && <Link to="/directory" data-testid="featured-view-all" className="text-sm font-medium text-[hsl(var(--primary))]">
                {t("home.allShopsLink")}
              </Link>}
            </div>
            {loading ? (
              <div className="mt-3.5 grid gap-4 sm:grid-cols-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-56 rounded-2xl" />
                ))}
              </div>
            ) : featuredBusinesses.length === 0 ? (
              <div className="mt-3.5 rounded-2xl border border-dashed border-border p-6 text-center" data-testid="home-shops-empty">
                <p className="text-sm text-muted-foreground">{t("home.shopsEmpty", { city: cityName })}</p>
                <Link
                  to="/onboarding-request"
                  data-testid="home-shops-empty-cta"
                  aria-disabled={suspended}
                  onClick={(e) => suspended && e.preventDefault()}
                  className={`mt-3 inline-block rounded-full bg-[hsl(var(--accent))] px-5 py-2 text-sm font-semibold text-accent-foreground ${suspended ? disabledCtaClass : ""}`}
                >
                  {t("home.shopsEmptyCta")}
                </Link>
              </div>
            ) : (
              <>
                <div className="mt-3.5 grid gap-4 sm:grid-cols-2">
                  {featuredBusinesses.map((b) => (
                    <BusinessCard key={b.id} business={b} showHours />
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground" data-testid="home-premium-caption">{t("home.premiumCaption")}</p>
              </>
            )}
          </section>

          <section>
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-heading text-xl font-semibold tracking-tight">{t("home.happeningTitle", { city: cityName })}</h2>
              <Link to="/activity" className="text-sm font-medium text-[hsl(var(--primary))]">
                {t("home.myCityFeedLink")}
              </Link>
            </div>
            {loading ? (
              <div className="mt-3.5 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 rounded-2xl" />
                ))}
              </div>
            ) : feedRows.length === 0 ? (
              <p className="mt-3.5 rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="home-feed-empty">
                {t("home.feedEmpty", { city: cityName })}
              </p>
            ) : (
              <div className="mt-3.5 flex flex-col gap-3" data-testid="home-feed">
                {feedRows.map(({ key, item }, idx) => {
                  const { title, meta } = feedRowText(key, item, lang, t);
                  const to = key === "events" ? `/events/${item.id}` : key === "offers" ? advertisedBusinessPath({ slug: item.business_slug }) : advertisedBusinessPath(item);
                  return (
                    <Link
                      key={`${key}-${item.id}`}
                      to={to}
                      data-testid={`home-feed-row-${idx}`}
                      className="flex gap-3.5 rounded-2xl border border-border bg-card p-4 transition-transform duration-200 hover:-translate-y-0.5"
                    >
                      <span className={`w-16 shrink-0 pt-0.5 font-mono text-[11px] ${FEED_KICKER_TONE[key]}`}>{t(`home.${FEED_KICKERS[key]}`)}</span>
                      <span className="min-w-0">
                        <span className="block text-[15px] font-semibold leading-snug">{title}</span>
                        {meta && <span className="mt-0.5 block truncate text-[13px] text-muted-foreground">{meta}</span>}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <aside className="flex min-w-0 flex-col gap-5">
          {/* Sponsored rail card (e.g. "Book a doctor") */}
          <CityAdSlot ad={ads.rail} slot="rail" testId="home-ad-rail" rate={ads.rates?.rail} showPlaceholder={canSellAds} canSellAds={canSellAds} />

          {availableWorkers.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="home-need-hand">
              <h3 className={`text-xs font-semibold uppercase text-muted-foreground ${lang === "en" ? "tracking-[0.12em]" : ""}`}>{t("home.needHand")}</h3>
              <div className="mt-3 flex flex-col gap-2.5">
                {availableWorkers.map((w) => (
                  <div key={w.id} className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold">{w.name} · {tradeLabel(lang, w)}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {w.rating_count > 0 ? `★ ${Number(w.rating_avg).toFixed(1)}` : t("common.noReviews")} ·{" "}
                        {t("directory.experience", { years: w.experience_years, count: w.experience_years })} ·{" "}
                        <span className="font-semibold text-[hsl(var(--primary))]">{t("home.availableToday")}</span>
                      </p>
                    </div>
                    {w.phone && (
                      <a href={`tel:${w.phone}`} className="rounded-full border border-border px-3.5 py-1.5 text-xs font-semibold">
                        {t("common.call")}
                      </a>
                    )}
                  </div>
                ))}
              </div>
              <Link
                to="/services"
                data-testid="home-post-task"
                aria-disabled={suspended}
                onClick={(e) => suspended && e.preventDefault()}
                className={`mt-3.5 block rounded-full border border-[hsl(var(--primary))]/35 bg-[hsl(var(--primary))]/8 py-2 text-center text-sm font-semibold text-[hsl(var(--primary))] ${suspended ? disabledCtaClass : ""}`}
              >
                {t("home.postTask")}
              </Link>
            </div>
          )}

          {jobs.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="home-jobs">
              <h3 className={`text-xs font-semibold uppercase text-muted-foreground ${lang === "en" ? "tracking-[0.12em]" : ""}`}>{t("home.jobsInCity", { city: cityName })}</h3>
              <div className="mt-3 flex flex-col gap-2.5 text-sm">
                {jobs.slice(0, 2).map((j) => (
                  <Link key={j.id} to={`/jobs/${j.id}`} className="block">
                    <p className="font-semibold">{localized(lang, j, "title")} · {j.employer_name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[payText(j, t), t(`jobs.types.${j.job_type}`)].filter(Boolean).join(" · ")}
                    </p>
                  </Link>
                ))}
              </div>
              <Link to="/jobs" className="mt-3 block text-sm font-medium text-[hsl(var(--primary))]">
                {t("home.allOpenings", { count: jobs.length })}
              </Link>
            </div>
          )}

          {landmarks.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="home-explore">
              <h3 className={`text-xs font-semibold uppercase text-muted-foreground ${lang === "en" ? "tracking-[0.12em]" : ""}`}>{t("home.exploreTitle", { city: cityName })}</h3>
              <div className="mt-3 flex flex-col gap-2.5 text-sm">
                {landmarks.slice(0, 3).map((l) => (
                  <Link key={l.id} to={`/place/${l.slug}`} className="block">
                    <p className="font-semibold">{localized(lang, l, "name")}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl bg-[hsl(var(--brand-deep))] p-5">
            <h3 className="font-heading text-[17px] font-semibold text-[var(--brand-cream,#f7f3ec)]">{t("home.runShop", { city: cityName })}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[var(--brand-cream,#f7f3ec)]">{t("home.runShopBody")}</p>
            <Link
              to="/onboarding-request"
              data-testid="home-list-my-shop"
              aria-disabled={suspended}
              onClick={(e) => suspended && e.preventDefault()}
              className={`mt-3.5 inline-block rounded-full bg-[hsl(var(--accent))] px-5 py-2 text-sm font-semibold text-accent-foreground ${suspended ? disabledCtaClass : ""}`}
            >
              {t("home.listMyShop")}
            </Link>
          </div>
        </aside>
      </div>

      <CityAbout
        loading={loading}
        about={about}
        landmarks={landmarks}
        adSlot={<CityAdSlot ad={ads.about} slot="about" testId="home-ad-about" rate={ads.rates?.about} showPlaceholder={canSellAds} canSellAds={canSellAds} />}
      />

      <section className="mx-auto w-full max-w-7xl px-5 py-10 sm:px-8">
        <details open={howOpen} className="group">
          <summary className="cursor-pointer list-none font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            {t("home.howItWorksToggle")}
          </summary>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {[
              { n: "01", title: t("home.howOne"), body: t("home.howOneBody") },
              { n: "02", title: t("home.howTwo"), body: t("home.howTwoBody") },
              { n: "03", title: t("home.howThree"), body: t("home.howThreeBody") },
            ].map((s) => (
              <div key={s.n} className="border-l-2 border-[hsl(var(--accent))] pl-5">
                <span className="font-mono text-xs text-[hsl(var(--accent))]">{s.n}</span>
                <h3 className="mt-2 font-heading text-xl font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </details>
      </section>
    </div>
  );
}
